import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search,
  Medication,
  LocalPharmacy,
  Pets,
  Science,
  Description,
  Close,
  Info
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import SectionHeader from '@shared/SectionHeader';
import FilterBar, { FilterOption } from '@components/shared/FilterBar';
import PaginatedList from '@shared/PaginatedList';
import EmptyState from '@shared/EmptyState';
import vetProductsService, {
  VeterinaryProduct,
  VetProductFilters,
  VetProductFilterOptions
} from '@services/vetProductsService';

const VetProductsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState<VeterinaryProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Paginare
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // Filtre
  const [filters, setFilters] = useState<VetProductFilters>({
    search: searchParams.get('search') || '',
    species: searchParams.get('species') || '',
    active_substance: searchParams.get('active_substance') || '',
    pharmaceutical_form: searchParams.get('pharmaceutical_form') || '',
    prescription_mode: searchParams.get('prescription_mode') as 'Cu prescripție' | 'Fără prescripție' | undefined,
    category: searchParams.get('category') || '',
    license_holder: searchParams.get('license_holder') || ''
  });

  // Opțiuni pentru filtre
  const [filterOptions, setFilterOptions] = useState<VetProductFilterOptions>({
    species: [],
    active_substances: [],
    pharmaceutical_forms: [],
    categories: [],
    license_holders: []
  });

  // Dialog pentru detalii produs
  const [selectedProduct, setSelectedProduct] = useState<VeterinaryProduct | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Încarcă opțiunile pentru filtre
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await vetProductsService.getFilterOptions();
        setFilterOptions(options);
      } catch (err: any) {
        console.error('Error loading filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Construiește opțiunile pentru FilterBar
  const filterOptionsForBar: FilterOption[] = [
    {
      key: 'search',
      label: 'Căutare',
      type: 'text',
      placeholder: 'Caută după nume produs sau licențiar...'
    },
    {
      key: 'species',
      label: 'Specie țintă',
      type: 'select',
      options: [
        { value: '', label: 'Toate speciile' },
        ...filterOptions.species.map(s => ({ value: s.name, label: s.name }))
      ],
      placeholder: 'Selectează specie'
    },
    {
      key: 'active_substance',
      label: 'Substanță activă',
      type: 'select',
      options: [
        { value: '', label: 'Toate substanțele' },
        ...filterOptions.active_substances.slice(0, 100).map(s => ({ value: s.name, label: s.name }))
      ],
      placeholder: 'Selectează substanță activă'
    },
    {
      key: 'pharmaceutical_form',
      label: 'Formă farmaceutică',
      type: 'select',
      options: [
        { value: '', label: 'Toate formele' },
        ...filterOptions.pharmaceutical_forms.map(f => ({ value: f, label: f }))
      ],
      placeholder: 'Selectează formă'
    },
    {
      key: 'prescription_mode',
      label: 'Mod eliberare',
      type: 'select',
      options: [
        { value: '', label: 'Toate' },
        { value: 'Cu prescripție', label: 'Cu prescripție' },
        { value: 'Fără prescripție', label: 'Fără prescripție' }
      ],
      placeholder: 'Selectează mod'
    },
    {
      key: 'category',
      label: 'Categorie',
      type: 'select',
      options: [
        { value: '', label: 'Toate categoriile' },
        ...filterOptions.categories.map(c => ({ value: c, label: c }))
      ],
      placeholder: 'Selectează categorie'
    }
  ];

  // Încarcă produsele
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiFilters: VetProductFilters = {
        page: currentPage,
        limit: itemsPerPage,
        ...filters
      };

      const result = await vetProductsService.getProducts(apiFilters);

      setProducts(result.data);
      setTotalItems(result.pagination.total);
      setTotalPages(result.pagination.totalPages);

      // Actualizează URL-ul cu parametrii de căutare
      const newSearchParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, String(value));
        }
      });
      if (currentPage > 1) {
        newSearchParams.set('page', String(currentPage));
      }
      setSearchParams(newSearchParams, { replace: true });

    } catch (err: any) {
      console.error('Error loading products:', err);
      setError(err.message || 'Eroare la încărcarea produselor');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, setSearchParams]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setFilters({
      search: '',
      species: '',
      active_substance: '',
      pharmaceutical_form: '',
      prescription_mode: undefined,
      category: '',
      license_holder: ''
    });
    setCurrentPage(1);
  };

  const handleProductClick = async (productId: string) => {
    try {
      const product = await vetProductsService.getProductById(productId);
      setSelectedProduct(product);
      setDetailDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading product details:', err);
      setError(err.message || 'Eroare la încărcarea detaliilor produsului');
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'antiparazitar_extern': return 'warning';
      case 'vaccin': return 'success';
      case 'antibiotic': return 'error';
      case 'analgezic': return 'info';
      case 'anestezie': return 'secondary';
      default: return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'antiparazitar_extern': return 'Antiparazitar extern';
      case 'vaccin': return 'Vaccin';
      case 'antibiotic': return 'Antibiotic';
      case 'analgezic': return 'Analgezic';
      case 'anestezie': return 'Anestezie';
      default: return 'General';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <SectionHeader
        title="Produse Medicinale Veterinare"
        subtitle="Căutare și informații despre produsele medicinale autorizate"
        badge={{
          count: totalItems,
          color: 'primary'
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtre */}
      <FilterBar
        filters={filterOptionsForBar}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <EmptyState
          title="Nu există produse"
          description="Nu am găsit produse pentru filtrele selectate. Încearcă să modifici criteriile de căutare."
          icon={<Medication color="disabled" sx={{ fontSize: 60 }} />}
        />
      ) : (
        <>
          <PaginatedList
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          >
            <Grid container spacing={3}>
              {products.map((product) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleProductClick(product.id)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Header cu nume și categorie */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Typography variant="h6" component="h3" fontWeight={600} sx={{ flex: 1, mr: 1 }}>
                          {product.commercial_name}
                        </Typography>
                        <Chip
                          label={getCategoryLabel(product.category)}
                          color={getCategoryColor(product.category) as any}
                          size="small"
                        />
                      </Box>

                      {/* Licențiar */}
                      <Box display="flex" alignItems="center" mb={2}>
                        <LocalPharmacy sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {product.license_holder}
                        </Typography>
                      </Box>

                      {/* Substante active */}
                      {product.active_substances && product.active_substances.length > 0 && (
                        <Box display="flex" alignItems="flex-start" mb={2}>
                          <Science sx={{ fontSize: 18, mr: 1, color: 'text.secondary', mt: 0.5 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Substante active:
                            </Typography>
                            <Typography variant="body2">
                              {product.active_substances.slice(0, 2).map(s => s.name).join(', ')}
                              {product.active_substances.length > 2 && ` +${product.active_substances.length - 2}`}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Specii țintă */}
                      {product.target_species && product.target_species.length > 0 && (
                        <Box display="flex" alignItems="flex-start" mb={2}>
                          <Pets sx={{ fontSize: 18, mr: 1, color: 'text.secondary', mt: 0.5 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Specii țintă:
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {product.target_species.slice(0, 3).map((species) => (
                                <Chip
                                  key={species.id}
                                  label={species.name}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                              {product.target_species.length > 3 && (
                                <Chip
                                  label={`+${product.target_species.length - 3}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Formă farmaceutică */}
                      {product.pharmaceutical_form && (
                        <Box display="flex" alignItems="center" mb={2}>
                          <Description sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {product.pharmaceutical_form}
                          </Typography>
                        </Box>
                      )}

                      {/* Mod prescripție */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt="auto">
                        <Chip
                          label={product.prescription_mode}
                          color={product.prescription_mode === 'Fără prescripție' ? 'success' : 'warning'}
                          size="small"
                          variant="outlined"
                        />
                        <Button
                          size="small"
                          endIcon={<Info />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProductClick(product.id);
                          }}
                        >
                          Detalii
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </PaginatedList>
        </>
      )}

      {/* Dialog pentru detalii produs */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={600}>
              {selectedProduct?.commercial_name}
            </Typography>
            <IconButton onClick={() => setDetailDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <>
              {/* Licențiar */}
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Licențiar
                </Typography>
                <Typography variant="body1">{selectedProduct.license_holder}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Substante active */}
              {selectedProduct.active_substances && selectedProduct.active_substances.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Substante Active
                  </Typography>
                  <List dense>
                    {selectedProduct.active_substances.map((substance) => (
                      <ListItem key={substance.id}>
                        <ListItemText
                          primary={substance.name}
                          secondary={substance.concentration || substance.description || ''}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Specii țintă */}
              {selectedProduct.target_species && selectedProduct.target_species.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Specii Țintă
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedProduct.target_species.map((species) => (
                      <Chip
                        key={species.id}
                        label={species.name}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Informații suplimentare */}
              <Grid container spacing={2}>
                {selectedProduct.pharmaceutical_form && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Formă Farmaceutică
                    </Typography>
                    <Typography variant="body1">{selectedProduct.pharmaceutical_form}</Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Mod Eliberare
                  </Typography>
                  <Chip
                    label={selectedProduct.prescription_mode}
                    color={selectedProduct.prescription_mode === 'Fără prescripție' ? 'success' : 'warning'}
                    size="small"
                  />
                </Grid>
                {selectedProduct.category && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Categorie
                    </Typography>
                    <Chip
                      label={getCategoryLabel(selectedProduct.category)}
                      color={getCategoryColor(selectedProduct.category) as any}
                      size="small"
                    />
                  </Grid>
                )}
                {selectedProduct.authorization_number && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Număr Autorizare
                    </Typography>
                    <Typography variant="body1">{selectedProduct.authorization_number}</Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VetProductsPage;

